import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { resolvePunditImageUrl } from '../utils/imageUrl';
import { Link } from 'react-router-dom';

const defaultHero = {
  badge: 'Divine Services at Your Doorstep',
  headline: 'Experience the Essence of',
  highlight: 'Vedic Rituals',
  subtitle: 'Book trusted Pandits, perform sacred Pujas, generate your free Kundli, and find auspicious Muhurat guidance.',
  trustLine: 'Our Pandits are highly qualified industry leaders. We perform Poojas with passion and devotion—not as a profession.',
  backgroundImage: '',
  stats: [
    { value: '50+', label: 'Verified Pundits' },
    { value: '100+', label: 'Pooja Items' },
    { value: '20+', label: 'Happy Devotees' },
  ],
};

const Hero = ({ onSearch }) => {
  const [hero, setHero] = useState(defaultHero);
  const [checkoutSettings, setCheckoutSettings] = useState({ paymentGatewayEnabled: true });
  const [activeImage, setActiveImage] = useState(0);
  const [portraitSlides, setPortraitSlides] = useState({});
  const [failedSlides, setFailedSlides] = useState({});

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/punditpurohit/hero`)
      .then((res) => res.json())
      .then((data) => setHero({ ...defaultHero, ...data }))
      .catch((err) => {
        console.error('Unable to load Pundit Purohit hero settings', err);
        setHero(defaultHero);
      });

    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/punditpurohit/checkout-settings`)
      .then((res) => res.json())
      .then((data) => setCheckoutSettings({
        paymentGatewayEnabled: Boolean(data?.paymentGatewayEnabled),
      }))
      .catch((err) => {
        console.error('Unable to load Pundit Purohit checkout settings', err);
        setCheckoutSettings({ paymentGatewayEnabled: true });
      });
  }, []);

  const configuredHeroImages = (hero.backgroundImages?.length ? hero.backgroundImages : [hero.backgroundImage]).filter(Boolean);
  const heroImages = configuredHeroImages.filter((image) => !failedSlides[resolvePunditImageUrl(image)]);
  const activeImageUrl = resolvePunditImageUrl(heroImages[activeImage]);

  useEffect(() => {
    setActiveImage(0);
    setPortraitSlides({});
    setFailedSlides({});
  }, [hero.backgroundImages, hero.backgroundImage]);

  useEffect(() => {
    setActiveImage((current) => Math.min(current, Math.max(heroImages.length - 1, 0)));
  }, [heroImages.length]);

  useEffect(() => {
    if (heroImages.length < 2) return undefined;
    const timer = window.setInterval(() => setActiveImage((current) => (current + 1) % heroImages.length), 5000);
    return () => window.clearInterval(timer);
  }, [heroImages.length]);

  return (
    <div className={`pp-hero ${portraitSlides[activeImageUrl] ? 'pp-hero-portrait' : ''}`}>
      <div className="pp-hero-visual">
        {activeImageUrl && (
          <>
            <div
              className="pp-hero-backdrop"
              style={{ backgroundImage: `url("${activeImageUrl}")` }}
              aria-hidden="true"
            />
            <img
              className="pp-hero-media"
              src={activeImageUrl}
              alt=""
              onLoad={(event) => {
                const { naturalHeight, naturalWidth } = event.currentTarget;
                setPortraitSlides((current) => ({
                  ...current,
                  [activeImageUrl]: naturalHeight > naturalWidth,
                }));
              }}
              onError={() => setFailedSlides((current) => ({ ...current, [activeImageUrl]: true }))}
            />
          </>
        )}
        <div className="hero-overlay"></div>
        {heroImages.length > 1 && (
          <div className="pp-hero-carousel-controls" aria-label="Hero banner slides">
            {heroImages.map((image, index) => (
              <button type="button" key={image} className={index === activeImage ? 'active' : ''} onClick={() => setActiveImage(index)} aria-label={`Show banner ${index + 1}`} />
            ))}
          </div>
        )}
      </div>
      
      <motion.div 
        className="hero-content"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <span className="hero-badge">{hero.badge}</span>
        
        <h1>{hero.headline} <br /><span>{hero.highlight}</span></h1>
        
        <p>{hero.subtitle}</p>
        {hero.trustLine && <div className="hero-trust-line">{hero.trustLine}</div>}
        <div className="hero-cta-group"><Link className="hero-cta-primary" to="/book-pandit">Book a Pandit</Link><div><Link className="hero-cta-secondary" to="/astrology/kundli">Get Your Free Kundli</Link><p className="hero-free-note">Free Vedic birth chart • No payment required</p></div></div>

        {!checkoutSettings.paymentGatewayEnabled && (
          <div className="hero-zero-cost-banner">
            <strong>Book your pooja at zero cost today</strong>
            <span>Limited-time no online payment offer. Reserve your preferred ritual slot now.</span>
          </div>
        )}
        
        {/* Only ONE search bar here, correctly connected to onSearch */}
        {/* <div className="hero-search">
          <input 
            type="text" 
            placeholder="Search for Poojas, Pundits, or Items..." 
            onChange={(e) => onSearch(e.target.value)} 
          />
          <button className="primary-btn">Search</button>
        </div> */}

        <div className="hero-stats">
          {(hero.stats || []).map((stat, index) => (
            <div className="stat-item" key={`${stat.value}-${stat.label}-${index}`}>
              <strong>{stat.value}</strong>
              <span>{stat.label}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
};

export default Hero;
